import { Box, makeStyles, TextField } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import * as React from "react";

import theme from "@/theme";

import { BottomSheet } from "../BottomSheet";
import { FormErrorText } from "../FormErrorText";

import { FormRow } from "./FormRow";

const useStyles = makeStyles(() => ({
  label: { display: "block", fontSize: 14, fontWeight: 500, marginBottom: 8 },
  trigger: {
    width: "100%",
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    border: "1.5px solid transparent",
    borderRadius: 8,
    padding: "0 16px",
    backgroundColor: theme.palette.semantic["field/bg"],
    font: "inherit",
    fontSize: 14,
    color: theme.palette.text.primary,
    cursor: "pointer",
    textAlign: "left",
    "&:focus-visible": {
      borderColor: theme.palette.primary.main,
      outline: `3px solid ${theme.palette.semantic["state/selected-bg"]}`,
    },
  },
  error: { backgroundColor: theme.palette.semantic["status/error-bg"], borderColor: theme.palette.error.main },
  placeholder: { color: theme.palette.semantic["text/placeholder"] },
  chevron: {
    width: 10,
    height: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& img": { display: "block" },
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 12px",
    minHeight: 53,
    border: 0,
    borderBottom: `1px solid ${theme.palette.semantic["border/default"]}`,
    background: "none",
    color: theme.palette.text.primary,
    font: "inherit",
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left",
    gap: 8,
    overflowWrap: "anywhere",
  },
  selected: { backgroundColor: theme.palette.semantic["state/selected-bg"], color: theme.palette.primary.main },
  search: { marginBottom: 16, width: "100%" },
}));

type Option = { value: string | number; label: string };
type Props = {
  label: string;
  value: string | number | null;
  options: Option[];
  onChange: (value: string | number) => void;
  onBlur?: () => void;
  error?: string;
  row?: boolean;
  searchable?: boolean;
  openPicker?: () => void;
  sheetZIndex?: number;
};

export const PickerField = React.forwardRef<HTMLButtonElement, Props>(
  ({ label, value, options, onChange, onBlur, error, row, searchable, openPicker, sheetZIndex }, ref) => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const selected = options.find((option) => option.value === value)?.label;
    const close = () => {
      setOpen(false);
      onBlur?.();
    };
    const start = () => {
      if (openPicker) {
        openPicker();
        return;
      }
      setQuery("");
      setOpen(true);
    };
    const errorId = `picker-${label}-error`;
    return (
      <Box mb={row ? 0 : 2}>
        {row ? (
          <FormRow ref={ref} label={label} value={selected} error={error} onClick={start} onBlur={onBlur} />
        ) : (
          <>
            <span className={classes.label}>{label}</span>
            <button
              ref={ref}
              type="button"
              aria-label={label}
              aria-haspopup="dialog"
              aria-invalid={!!error}
              aria-describedby={error ? errorId : undefined}
              onClick={start}
              onBlur={onBlur}
              className={`${classes.trigger} ${error ? classes.error : ""}`}
            >
              <span className={!selected ? classes.placeholder : undefined}>{selected || "選択してください"}</span>
              {error ? (
                <ErrorOutlineIcon style={{ fontSize: 16, color: theme.palette.error.main }} />
              ) : (
                <span className={classes.chevron} aria-hidden="true">
                  <img src="/icons/ds-v2-select-chevron.svg" alt="" />
                </span>
              )}
            </button>
            {error && <FormErrorText id={errorId}>{error}</FormErrorText>}
          </>
        )}
        {!openPicker && (
          <BottomSheet open={open} title={label} onOpen={start} onClose={close} maxHeightPercent={70} zIndex={sheetZIndex}>
            {searchable && (
              <TextField
                className={classes.search}
                variant="outlined"
                placeholder="絞り込み"
                inputProps={{ "aria-label": `${label}を絞り込み` }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div role="listbox" aria-label={label}>
              {options
                .filter((option) => option.label.includes(query))
                .map((option) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    key={option.value}
                    className={`${classes.option} ${option.value === value ? classes.selected : ""}`}
                    onClick={() => {
                      onChange(option.value);
                      close();
                    }}
                  >
                    <span>{option.label}</span>
                    {option.value === value && <CheckIcon style={{ fontSize: 16 }} />}
                  </button>
                ))}
            </div>
          </BottomSheet>
        )}
      </Box>
    );
  }
);

PickerField.displayName = "PickerField";
